import type { CrossContextReport } from './types';
import { stableStringify } from './hash';

/**
 * Cross-context fingerprint comparison.
 *
 * The single highest-value anti-detect check: automation frameworks and
 * anti-detect browsers frequently patch the *main* page context but forget the
 * iframe or worker context (or vice versa). A genuine browser reports identical
 * navigator/device values in all three.
 *
 * We run a tiny read-only snippet in three contexts and diff the results:
 *   - main   (window)
 *   - iframe (same-origin about:blank, so no network/CORS concerns)
 *   - worker (blob module worker)
 *
 * A mismatch is strong evidence of partial spoofing, so each divergence becomes
 * a finding by the caller.
 */

const SNIPPET = `
self.onmessage = () => {
  const n = self.navigator || {};
  const out = {
    userAgent: (() => { try { return n.userAgent; } catch { return null; } })(),
    platform: (() => { try { return n.platform; } catch { return null; } })(),
    vendor: (() => { try { return n.vendor; } catch { return null; } })(),
    hardwareConcurrency: (() => { try { return n.hardwareConcurrency; } catch { return null; } })(),
    deviceMemory: (() => { try { return n.deviceMemory; } catch { return null; } })(),
    languages: (() => { try { return n.languages ? Array.from(n.languages) : null; } catch { return null; } })(),
    webdriver: (() => { try { return n.webdriver; } catch { return null; } })(),
    timezone: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; } })(),
  };
  self.postMessage(out);
};
`;

const readMain = (): Record<string, unknown> => {
  const n = navigator;
  return {
    userAgent: n.userAgent,
    platform: n.platform,
    vendor: n.vendor,
    hardwareConcurrency: n.hardwareConcurrency,
    deviceMemory: (n as unknown as { deviceMemory?: number }).deviceMemory ?? null,
    languages: n.languages ? Array.from(n.languages) : null,
    webdriver: n.webdriver,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

function runWorker(timeoutMs = 2000): Promise<Record<string, unknown> | null> {
  return new Promise((resolve) => {
    try {
      const blob = new Blob([SNIPPET], { type: 'text/javascript' });
      const url = URL.createObjectURL(blob);
      const worker = new Worker(url);
      const cleanup = () => {
        worker.terminate();
        URL.revokeObjectURL(url);
      };
      const timer = setTimeout(() => {
        cleanup();
        resolve(null);
      }, timeoutMs);
      worker.onmessage = (e: MessageEvent) => {
        clearTimeout(timer);
        cleanup();
        resolve(e.data as Record<string, unknown>);
      };
      worker.onerror = () => {
        clearTimeout(timer);
        cleanup();
        resolve(null);
      };
      worker.postMessage('go');
    } catch {
      resolve(null);
    }
  });
}

function runIframe(timeoutMs = 2000): Promise<Record<string, unknown> | null> {
  return new Promise((resolve) => {
    let iframe: HTMLIFrameElement | null = null;
    const timer = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);
    const cleanup = () => {
      if (iframe) {
        iframe.remove();
        iframe = null;
      }
    };
    try {
      iframe = document.createElement('iframe');
      // srcdoc keeps it same-origin without a network request.
      iframe.srcdoc = '<!doctype html><html><head><meta charset="utf-8"></head><body></body></html>';
      iframe.style.display = 'none';
      iframe.onload = () => {
        try {
          const w = iframe?.contentWindow;
          if (!w) throw new Error('no contentWindow');
          const n = w.navigator;
          const data = {
            userAgent: n.userAgent,
            platform: n.platform,
            vendor: n.vendor,
            hardwareConcurrency: n.hardwareConcurrency,
            deviceMemory: (n as unknown as { deviceMemory?: number }).deviceMemory ?? null,
            languages: n.languages ? Array.from(n.languages) : null,
            webdriver: n.webdriver,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          };
          clearTimeout(timer);
          cleanup();
          resolve(data);
        } catch {
          clearTimeout(timer);
          cleanup();
          resolve(null);
        }
      };
      document.body.appendChild(iframe);
    } catch {
      clearTimeout(timer);
      cleanup();
      resolve(null);
    }
  });
}

/** Runs the snippet in all three contexts and reports disagreements. */
export async function collectCrossContext(): Promise<CrossContextReport> {
  const [main, iframe, worker] = await Promise.all([Promise.resolve(readMain()), runIframe(), runWorker()]);

  const contexts: CrossContextReport['contexts'] = ['main'];
  if (iframe) contexts.push('iframe');
  if (worker) contexts.push('worker');

  const keys = new Set<string>(Object.keys(main));
  if (iframe) Object.keys(iframe).forEach((k) => keys.add(k));
  if (worker) Object.keys(worker).forEach((k) => keys.add(k));

  const divergences: CrossContextReport['divergences'] = [];
  for (const key of keys) {
    const mainV = main[key];
    const iframeV = iframe ? iframe[key] : undefined;
    const workerV = worker ? worker[key] : undefined;

    const all = [mainV, iframeV, workerV].filter((v) => v !== undefined);
    const serialized = all.map((v) => stableStringify(v));
    const allEqual = serialized.every((s) => s === serialized[0]);

    if (!allEqual) {
      divergences.push({ signal: key, main: mainV, iframe: iframeV, worker: workerV });
    }
  }

  return { available: contexts.length > 1, contexts, divergences };
}
